import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import BackToIssues from '../IssueDetails/BackToIssues';
import Text from '../../generic/Text';
import { exceptionsEnabledCategories, IssueScopeKind } from '../../../../shared/issuesV2';
import NotFound from '../../generic/NotFound';
import type { IssuesContainerProps } from '../IssuesContainer';
import IssueExceptionsTable from './IssueExceptionsTable';
import IssueExceptionsHeader from './IssueExceptionsHeader';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { ignoreOrResolveWordPast } from '../../../utils';
import type { UseIssueExceptionsProps } from './useIssueExceptions';
import useIssueExceptions from './useIssueExceptions';
import Flex from '../../generic/Flex';
import AddIgnoreRuleButton from './AddIgnoreRuleButton';
import { FeatureFlagRequired } from '../../generic';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import { DISABLE_PACKAGE_LABELS } from '../../../../shared/featureFlags/flagNames';
import { isFreeTier } from '../../../../shared/subscriptions';
import { window } from '../../../globals';

type Props = Pick<IssuesContainerProps, 'params'>;

const IssueExceptionsPage: VoidFunctionComponent<Props> = ({
  params,
}) => {
  const { category } = params;
  const { type, id } = useSelector(IssuesV2Store.selectors.getScope);

  const projectFilter = type === IssueScopeKind.Project ? { projectId: id } : {};
  const releaseGroupFilter = type === IssueScopeKind.ReleaseGroup ? { releaseGroupId: id } : {};

  const tableProps = {
    category: category as UseIssueExceptionsProps['category'],
    ...projectFilter,
    ...releaseGroupFilter,
  } as UseIssueExceptionsProps;

  const {
    page,
    count,
    search,
    sortBy,
    setPage,
    setCount,
    setSearch,
    setSort,
    onRemoveExceptions,
    onExtendException,
  } = useIssueExceptions(tableProps);

  // Gate this page to only show for categories that have exceptions implemented
  if (!exceptionsEnabledCategories.includes(category)) {
    return <NotFound />;
  }

  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  return (
    <div className="issue-exceptions" data-test-id="issue-exceptions">
      <BackToIssues />

      <Flex justify="space-between">
        <div className="issue-exceptions__header ">
          <Text.H1>Ignore Rules ({category})</Text.H1>
          <Text>
            These rules determine when {category} issues should be {ignoreOrResolveWordPast().toLowerCase()}{' '}
            automatically.
          </Text>
        </div>

        {type !== IssueScopeKind.ReleaseGroup && (
          <PaidFeatureGate isRestricted={isOrgFreeTier}>
            <FeatureFlagRequired flagName={DISABLE_PACKAGE_LABELS} isNotEnabled>
              <AddIgnoreRuleButton projectId={tableProps.projectId} />
            </FeatureFlagRequired>
          </PaidFeatureGate>
        )}
      </Flex>

      <div className="issue-exceptions__content margin-top-md">
        <IssueExceptionsHeader
          page={page}
          count={count}
          setPage={setPage}
          search={search}
          sortBy={sortBy}
          onChangeSortBy={setSort}
          onChangeSearch={setSearch}
        />
        <IssueExceptionsTable
          category={tableProps.category}
          releaseGroupId={tableProps.releaseGroupId}
          projectId={tableProps.projectId}
          page={page}
          count={count}
          setPage={setPage}
          setCount={setCount}
          onRemoveExceptions={onRemoveExceptions}
          onExtendException={onExtendException}
        />
      </div>
    </div>
  );
};

export default IssueExceptionsPage;
